import { DEOnly } from '@iwIntlProvider/CountryWrappers';
import { CheckBox, ErrorMessage, Input, Select, Tooltip } from '@iwoca/orion';
import {
    Control,
    Controller,
    FieldErrors,
    UseFormRegister
} from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import styles from './PersonDetails.module.css';
import { useIsInSelfServeSplitTest } from '../../../../customHooks/useIsInSelfServeSplitTest';
import { renderFieldError } from '../../../../forms/helpers/renderFieldError';
import {
    titleMessages,
    titles,
    titlesDE
} from '../../../../forms/nameField/Titles';
import { months } from '../../../../helpers/months';
import { getCountry } from '../../../../iwIntlProvider/CountryProvider';
import { TPersonFormValues } from '../PersonModal.types';

type PersonDetailsProps = {
    register: UseFormRegister<TPersonFormValues>;
    control: Control<TPersonFormValues>;
    errors: FieldErrors<TPersonFormValues>;
    isGuarantor: boolean;
    isEditingPerson?: boolean;
    hasCreditDecision?: boolean;
};

const PersonDetails = ({
    register,
    control,
    errors,
    isGuarantor,
    isEditingPerson,
    hasCreditDecision
}: PersonDetailsProps) => {
    const intl = useIntl();
    const isInSelfServeSplitTest = useIsInSelfServeSplitTest();

    const personalDetailsEditingDisabled =
        hasCreditDecision && isEditingPerson && isGuarantor;

    const isDE = getCountry() === 'DE';

    const nameError = renderFieldError(errors.firstName || errors.lastName);
    const dobError = renderFieldError(
        errors.dateOfBirth?.day ||
            errors.dateOfBirth?.month ||
            errors.dateOfBirth?.year
    );

    return (
        <>
            <div className="mb-xl">
                <label className="mb-m block font-med">
                    <FormattedMessage
                        id="_.person.full_name"
                        defaultMessage="Full name"
                    />
                </label>
                <div className="flex gap-m">
                    <Select
                        {...register('title')}
                        wrapperClassName="max-w-[125px] flex-1"
                        data-testid="title-select"
                        defaultValue=""
                        disabled={!!personalDetailsEditingDisabled}
                    >
                        <option value="" disabled>
                            {intl.formatMessage({
                                id: '_.name_field.title',
                                defaultMessage: 'Title'
                            })}
                        </option>
                        {isDE
                            ? titlesDE.map(({ value, label }) => (
                                  <option key={value} value={value}>
                                      {label}
                                  </option>
                              ))
                            : titles.map(({ value, messageKey }) => (
                                  <option key={value} value={value}>
                                      {intl.formatMessage(
                                          titleMessages[messageKey]
                                      )}
                                  </option>
                              ))}
                    </Select>

                    {/* eslint-disable-next-line iwoca/input-a11y */}
                    <Input
                        {...register('firstName')}
                        wrapperClassName="flex-1"
                        data-testid="first-name-input"
                        placeholder={intl.formatMessage({
                            id: '_.name_field.first_name',
                            defaultMessage: 'First name'
                        })}
                        disabled={!!personalDetailsEditingDisabled}
                    />
                    {/* eslint-disable-next-line iwoca/input-a11y */}
                    <Input
                        {...register('lastName')}
                        wrapperClassName="flex-1"
                        data-testid="last-name-input"
                        placeholder={intl.formatMessage({
                            id: '_.name_field.last_name',
                            defaultMessage: 'Last name'
                        })}
                        disabled={!!personalDetailsEditingDisabled}
                    />
                </div>
                {nameError && (
                    <ErrorMessage className="mt-s">{nameError}</ErrorMessage>
                )}
            </div>

            <div className="mb-xl">
                <label className="mb-m block font-med">
                    <FormattedMessage
                        id="_.person.date_of_birth"
                        defaultMessage="Date of birth"
                    />
                </label>
                <div className="flex gap-m">
                    {/* eslint-disable-next-line iwoca/input-a11y */}
                    <Input
                        {...register('dateOfBirth.day')}
                        wrapperClassName="flex-1"
                        data-testid="dob-day-input"
                        placeholder={intl.formatMessage({
                            id: '_.date_input.day',
                            defaultMessage: 'Day'
                        })}
                        disabled={!!personalDetailsEditingDisabled}
                    />
                    <Select
                        {...register('dateOfBirth.month')}
                        wrapperClassName="flex-[1.5]"
                        data-testid="dob-month-select"
                        defaultValue=""
                        disabled={!!personalDetailsEditingDisabled}
                    >
                        <option value="" disabled>
                            {intl.formatMessage({
                                id: '_.date_input.month',
                                defaultMessage: 'Month'
                            })}
                        </option>
                        {months.map(({ value, label }) => (
                            <option key={value} value={value}>
                                {label}
                            </option>
                        ))}
                    </Select>
                    {/* eslint-disable-next-line iwoca/input-a11y */}
                    <Input
                        {...register('dateOfBirth.year')}
                        wrapperClassName="flex-[1.5]"
                        data-testid="dob-year-input"
                        placeholder={intl.formatMessage({
                            id: '_.date_input.year',
                            defaultMessage: 'Year'
                        })}
                        disabled={!!personalDetailsEditingDisabled}
                    />
                </div>
                {dobError && (
                    <ErrorMessage className="mt-s">{dobError}</ErrorMessage>
                )}
            </div>

            <div className="mb-xl">
                <label className="mb-m block font-med">
                    <FormattedMessage
                        id="_.person.role_title"
                        defaultMessage="This person is a..."
                    />
                </label>
                <Controller
                    control={control}
                    name="isDirector"
                    render={({ field: { onChange, value, name, ref } }) => (
                        <CheckBox
                            ref={ref}
                            name={name}
                            wrapperClassName={styles.checkboxGroup}
                            label={intl.formatMessage({
                                id: '_.company_role.director',
                                defaultMessage: 'Director'
                            })}
                            checked={!!value}
                            onChange={(e) => onChange(e.target.checked)}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="isShareholder"
                    render={({ field: { onChange, value, name, ref } }) => (
                        <CheckBox
                            ref={ref}
                            name={name}
                            wrapperClassName={styles.checkboxGroup}
                            label={intl.formatMessage({
                                id: '_.company_role.shareholder',
                                defaultMessage: 'Shareholder'
                            })}
                            checked={!!value}
                            onChange={(e) => onChange(e.target.checked)}
                        />
                    )}
                />
                <DEOnly>
                    <div>
                        <div>
                            <label>
                                <FormattedMessage
                                    id="_.person.additional_guarantor"
                                    defaultMessage="Additional Guarantor"
                                />
                            </label>
                            <Controller
                                control={control}
                                name="isGuarantor"
                                render={({
                                    field: { onChange, value, name, ref }
                                }) => (
                                    <CheckBox
                                        ref={ref}
                                        name={name}
                                        data-testid="isGuarantorDE"
                                        label={intl.formatMessage({
                                            id: '_.person.add_additional_guarantor',
                                            defaultMessage:
                                                'I also want to add this person as a guarantor'
                                        })}
                                        checked={!!value}
                                        onChange={(e) =>
                                            onChange(e.target.checked)
                                        }
                                    />
                                )}
                            />
                        </div>
                        {isGuarantor ? (
                            <div className="inline-block rounded-m bg-secondary-90 p-xl">
                                <FormattedMessage
                                    id="_.person.additional_guarantor_consent_msg"
                                    defaultMessage="In adding this person as a guarantor, I consent to the checking of their personal creditworthiness"
                                />
                                <Controller
                                    control={control}
                                    name="additionalGuarantorConsent"
                                    render={({
                                        field: { onChange, value, name, ref }
                                    }) => (
                                        <CheckBox
                                            ref={ref}
                                            name={name}
                                            wrapperClassName={
                                                styles.checkboxGroup
                                            }
                                            label={intl.formatMessage({
                                                id: '_.person.consent_confirmation',
                                                defaultMessage: 'I consent'
                                            })}
                                            checked={!!value}
                                            onChange={(e) =>
                                                onChange(e.target.checked)
                                            }
                                        />
                                    )}
                                />
                                {renderFieldError(
                                    errors.additionalGuarantorConsent
                                ) && (
                                    <ErrorMessage
                                        className="mt-s"
                                        data-testid="formErrors"
                                    >
                                        {renderFieldError(
                                            errors.additionalGuarantorConsent
                                        )}
                                    </ErrorMessage>
                                )}
                            </div>
                        ) : null}
                    </div>
                </DEOnly>
                {getCountry() === 'UK' ? (
                    <div>
                        <div
                            className={styles.guarantorCheckbox}
                            data-tip
                            data-for="contactTooltip"
                        >
                            <Controller
                                control={control}
                                name="isGuarantor"
                                render={({
                                    field: { onChange, value, name, ref }
                                }) => (
                                    <CheckBox
                                        ref={ref}
                                        name={name}
                                        wrapperClassName={styles.checkboxGroup}
                                        data-testid="isGuarantor"
                                        disabled={!!hasCreditDecision}
                                        label={intl.formatMessage({
                                            id: '_.company_role.guarantor',
                                            defaultMessage: 'Guarantor'
                                        })}
                                        checked={!!value}
                                        onChange={(e) =>
                                            onChange(e.target.checked)
                                        }
                                    />
                                )}
                            />
                            {hasCreditDecision && !isInSelfServeSplitTest ? (
                                <Tooltip
                                    content={intl.formatMessage({
                                        id: '_.person.tooltip_message',
                                        defaultMessage:
                                            'Contact your account manager to change this'
                                    })}
                                    placement="right"
                                    data-testid="contactTooltip"
                                />
                            ) : null}
                        </div>

                        {isGuarantor ? (
                            <div
                                className={styles.privacyPolicy}
                                data-testid="privacyPolicy"
                            >
                                <FormattedMessage
                                    id="_.person.privacy_policy"
                                    defaultMessage="I've shared the iwoca {privacyPolicyLink} with this person and they understand that their personal information will be used to run quotation searches with credit reference agencies, as well as fraud prevention agencies."
                                    values={{
                                        privacyPolicyLink: (
                                            <a
                                                href="https://www.iwoca.co.uk/privacy-policy/"
                                                rel="noopener noreferrer"
                                                target="_blank"
                                            >
                                                <FormattedMessage
                                                    id="_.person.privacy_policy_link"
                                                    defaultMessage="privacy policy"
                                                />
                                            </a>
                                        )
                                    }}
                                />
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default PersonDetails;
